import React from 'react';

import { Button, Group, LoadingOverlay, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconExternalLink, IconTags } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import useCreateLocation from 'hooks/location/useCreateLocation';
import useLocation from 'hooks/location/useLocation';
import useUpdateLocation from 'hooks/location/useUpdateLocation';
import { nameValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  locationId: string;
  name: string;
  isDefault: boolean;
  url: string | null | undefined;
}

interface IDrawerLocationFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  locationId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const DrawerLocationForm = ({
  formId,
  title,
  opened,
  closeHandler,
  locationId,
  type,
  permission = true,
}: IDrawerLocationFormProps): JSX.Element => {
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();

  const { data, isLoading, isFetching, isSuccess } = useLocation(locationId);

  const form = useForm<FormValues>({
    initialValues: { locationId: '', name: '', isDefault: false, url: '' },

    validate: {
      name: value =>
        !value || value.length > 120 ? 'Name must be between 1-120 characters' : nameValidator(value),
    },
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleUrlValueChange = (value: string | null): void => {
    var url = value;
    if (!!url && url.startsWith('http://')) {
      url = url.replace('http://', 'https://');
    } else if (!!url && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    form.setFieldValue('url', url);
  };

  const handleSubmit = (payload: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    if (type === 'edit') {
      updateMutation.mutate(
        {
          ...payload,
          url: payload.url || null,
        },
        {
          onError: error => {
            errorNotification('Location update failed', error.response?.data.message);
          },
          onSuccess: response => {
            successNotification('Location update success', `${response.data.name} successfully updated.`);
            handleCloseAndReset();
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          ...payload,
          url: payload.url || null,
          locationId: uuidv4(),
        },
        {
          onError: error => {
            errorNotification('Location creation failed', error.message);
          },
          onSuccess: response => {
            successNotification('Location creation success', `${response.data.name} successfully created.`);
            handleCloseAndReset();
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      form.setValues(data.data);
    }
  }, [data]);

  return (
    <Drawer
      opened={opened}
      title={title}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            disabled={!permission}
            form={formId}
            type='submit'
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading || isFetching}
            data-cy='saveLocation'
          >
            Save
          </Button>
        </Group>
      }
      onClose={handleCloseAndReset}
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            disabled={!permission}
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Location Name'
            data-cy='locationNameTextInput'
          />
          <TextInput
            disabled={!permission}
            label='URL'
            icon={<IconExternalLink />}
            placeholder='Set Location URL'
            value={form.values.url || undefined}
            onChange={e => handleUrlValueChange(e.currentTarget.value)}
          />
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerLocationForm;
